import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  signOut, 
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { UserProfile } from '../types';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  updateUserProfile: (profile: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const register = async (email: string, password: string, name: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update the user's display name
    await updateProfile(user, { displayName: name });
    
    // Create user profile in Firestore
    const newUserProfile: UserProfile = {
      id: user.uid,
      email: user.email!,
      name: name,
      createdAt: new Date(),
      fitnessLevel: 'iniciante-extremo',
      fitnessGoals: [],
      availableTime: 10,
      commitmentLevel: 1,
      currentActivity: '',
      exerciseHistory: '',
      preferences: {
        workoutTypes: []
      }
    };
    
    await setDoc(doc(db, 'users', user.uid), newUserProfile);
    setUserProfile(newUserProfile);
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };
  const loginWithGoogle = async () => {
    try {
      // Detectar ambiente de desenvolvimento
      const isDevelopment = process.env.NODE_ENV === 'development';
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');

      // Opções para forçar uso da produção em desenvolvimento
      const envForceProduction = process.env.REACT_APP_FORCE_PRODUCTION === 'true';
      const localStorageForceProduction = typeof window !== 'undefined' && 
        localStorage.getItem('forceProduction') === 'true';
      const forceProduction = envForceProduction || localStorageForceProduction;
      
      const useEmulators = isDevelopment && isLocalhost && !forceProduction;

      console.log('🔍 Login Google iniciado:', { 
        isDevelopment,
        isLocalhost,
        useEmulators,
        forceProduction: {
          env: envForceProduction,
          localStorage: localStorageForceProduction,
          final: forceProduction
        },
        hostname: window.location.hostname,
        nodeEnv: process.env.NODE_ENV 
      });

      // SEMPRE usar popup para melhor UX - sem redirect
      let result;
      
      if (useEmulators) {
        console.log('🧪 Desenvolvimento com emuladores: método popup');
        
        // Provider para emuladores (sem custom parameters)
        const emulatorProvider = new GoogleAuthProvider();
        result = await signInWithPopup(auth, emulatorProvider);
      } else {
        console.log('� Produção ou produção forçada: método popup');
        
        // Provider para produção com custom parameters
        const productionProvider = new GoogleAuthProvider();
        productionProvider.setCustomParameters({
          prompt: 'select_account'
        });
        
        result = await signInWithPopup(auth, productionProvider);
      }

      if (result) {
        console.log('✅ Login Google bem-sucedido via popup');
        await processUserLogin(result.user);
      }
    } catch (error: any) {
      console.error('❌ Erro detalhado no login Google:', {
        code: error.code,
        message: error.message,
        details: error
      });
      
      // Melhorar mensagens de erro para o usuário
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Login cancelado. Por favor, tente novamente.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup bloqueado pelo navegador. Por favor, permita popups e tente novamente.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Solicitação de popup cancelada. Aguarde um momento e tente novamente.');
      }
      
      throw error;
    }
  };

  // Função auxiliar para processar login do usuário
  const processUserLogin = async (user: User) => {
    try {
      console.log('👤 Processando login do usuário:', user.email);
      
      // Verificar se é um usuário novo ou existente
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        console.log('👶 Usuário novo - criando perfil');
        // Usuário novo - criar perfil
        const newUserProfile: UserProfile = {
          id: user.uid,
          email: user.email!,
          name: user.displayName || 'Usuário',
          createdAt: new Date(),
          fitnessLevel: 'iniciante-extremo',
          fitnessGoals: [],
          availableTime: 10,
          commitmentLevel: 1,
          currentActivity: '',
          exerciseHistory: '',
          preferences: {
            workoutTypes: []
          }
        };
        
        await setDoc(doc(db, 'users', user.uid), newUserProfile);
        setUserProfile(newUserProfile);
      } else {
        console.log('👨‍💼 Usuário existente - carregando perfil');
        // Usuário existente - carregar perfil
        const profile = userDoc.data() as UserProfile;
        setUserProfile(profile);
      }
      
      console.log('✅ Login processado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao processar login:', error);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
  };

  const updateUserProfile = async (profileUpdate: Partial<UserProfile>) => {
    if (!currentUser) return;
    
    const updatedProfile = { ...userProfile, ...profileUpdate };
    await setDoc(doc(db, 'users', currentUser.uid), updatedProfile);
    setUserProfile(updatedProfile as UserProfile);
  };
  const loadUserProfile = async (user: User) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data() as UserProfile;
        // Convert Firestore timestamp to Date
        if (profileData.createdAt && typeof profileData.createdAt !== 'object') {
          profileData.createdAt = new Date(profileData.createdAt);
        }
        setUserProfile(profileData);
      }
    } catch (error: any) {
      console.error('Erro ao carregar perfil do usuário:', error);
    }
  };

  useEffect(() => {
    // Listener para mudanças de autenticação
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔄 Auth state changed:', user?.email || 'nenhum usuário');
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);
  const value: AuthContextType = {
    currentUser,
    userProfile,
    login,
    loginWithGoogle,
    register,
    logout,
    loading,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
